import {
  empaquetar,
  desempaquetar,
  generarOffsetSesion,
  obfuscarNumero,
  desobfuscarNumero,
} from './obfuscation';
import { claveOpaca, type SemanticKey } from './keys';
import { TamperDetectedError } from './errors';

/**
 * Storage unificado: todas las escrituras/lecturas pasan por acá.
 * Responsable de:
 * - mantener el offset de sesión,
 * - empaquetar/desempaquetar blobs,
 * - detectar tampering y escalarlo como error.
 */

const SALT_KEY = claveOpaca('session_salt');

/** Devuelve el offset de sesión, creándolo si no existe. */
export function obtenerOffsetSesion(storage: Storage = localStorage): number {
  const raw = storage.getItem(SALT_KEY);
  if (raw !== null) {
    const parseado = parseInt(raw, 36);
    if (Number.isFinite(parseado) && parseado >= 0) {
      return parseado;
    }
  }
  const nuevo = generarOffsetSesion();
  storage.setItem(SALT_KEY, nuevo.toString(36));
  return nuevo;
}

/**
 * Guarda un valor obfuscado bajo una clave semántica.
 */
export function guardar<T>(clave: SemanticKey, valor: T, storage: Storage = localStorage): void {
  if (clave === 'session_salt') {
    throw new Error('session_salt es manejada internamente; no la uses directamente.');
  }
  const offset = obtenerOffsetSesion(storage);
  const keyOpaca = claveOpaca(clave);
  const blob = empaquetar(valor, offset, keyOpaca);
  storage.setItem(keyOpaca, JSON.stringify(blob));
}

export type CargarResultado<T> =
  | { status: 'ok'; valor: T }
  | { status: 'vacio' }
  | { status: 'incompatible'; mensaje: string };

/**
 * Carga un valor. Los resultados son:
 * - `ok`: data válida.
 * - `vacio`: no había nada guardado o el formato es ilegible (tratar como juego nuevo).
 * - `incompatible`: schema viejo (migrar o descartar).
 * - **Si se detecta tampering, se lanza `TamperDetectedError`** (no vuelve como resultado).
 */
export function cargar<T>(clave: SemanticKey, storage: Storage = localStorage): CargarResultado<T> {
  if (clave === 'session_salt') {
    throw new Error('session_salt es manejada internamente; no la uses directamente.');
  }
  const offset = obtenerOffsetSesion(storage);
  const keyOpaca = claveOpaca(clave);
  const raw = storage.getItem(keyOpaca);
  if (raw === null) {
    return { status: 'vacio' };
  }

  let blob: unknown;
  try {
    blob = JSON.parse(raw);
  } catch {
    return { status: 'vacio' };
  }

  const resultado = desempaquetar<T>(blob, offset, keyOpaca);
  if (resultado.ok) {
    return { status: 'ok', valor: resultado.valor };
  }
  if (resultado.razon === 'tampered') {
    throw new TamperDetectedError(clave, resultado.mensaje);
  }
  if (resultado.razon === 'version') {
    return { status: 'incompatible', mensaje: resultado.mensaje };
  }
  return { status: 'vacio' };
}

/** Borra una clave específica. */
export function borrar(clave: SemanticKey, storage: Storage = localStorage): void {
  storage.removeItem(claveOpaca(clave));
}

/** Borra absolutamente todo el save data. Usado en "nueva partida" y post-tamper. */
export function borrarTodo(storage: Storage = localStorage): void {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const k = storage.key(i);
    if (k && k.startsWith('_k_')) keys.push(k);
  }
  for (const k of keys) storage.removeItem(k);
}

// Re-export para conveniencia
export { obfuscarNumero, desobfuscarNumero };
